import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { isPossiblePhoneNumber } from "libphonenumber-js";

import { Card, Typography, Avatar, Box } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import NotificationSettings from "./NotificationSettings";
import UserDetails from "./UserDetails";
import LoactionDetails from "./LoactionDetails";

import useApi from "../../hooks/useApi";
import { useIsPWAInstalled } from "../../hooks/useIsPWAInstalled";
import { useStatusModal } from "../../context/StatusModalContext";
import { useUserStore } from "../../store/userStore";

import {
  isAndroid,
  isValidEmail,
  getChangedValues,
  removeEmptyKeys,
} from "../../utils/helpers";
import { requestNotificationPermission } from "../../firebase";

import Loader from "../../Components/Loader";
import ModalConfirmation from "../../Components/ModalConfirmation";
import BackButton from "../../Components/BackButton";
import CustomButton from "../../Components/CustomButton";
import BottomFixedContainer from "../../Components/BottomFixedContainer";

import "./style.scss";

const settingSections = {
  details: "details",
  notifications: "notifications",
  location: "location",
};

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { patch, loading } = useApi();
  const isPWA = useIsPWAInstalled();
  const { showStatusModal } = useStatusModal();
  const ps10005000 = JSON.parse(localStorage.getItem("ps10005000"));
  const decodedToken = jwtDecode(ps10005000);
  const { user, fetchUser } = useUserStore();
  const userRole = user?.type;

  const [selectedSection, setSelectedSection] = useState(null);
  const [userData, setUserData] = useState({});
  const [formData, setFormData] = useState({});
  const [termsIsChecked, setTermsIsChecked] = useState(false);
  const [termsHasError, setTermsHasError] = useState(false);
  const [phoneHasError, setPhoneHasError] = useState(false);
  const [confirmModalToggle, setConfirmModalToggle] = useState("");
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);

  const [emailNeedsVerification, setEmailNeedsVerification] = useState(false);
  const [mobileNeedsVerification, setMobileNeedsVerification] = useState(false);
  const [showEmailVerificationError, setShowEmailVerificationError] =
    useState(false);
  const [showPhoneVerificationError, setShowPhoneVerificationError] =
    useState(false);

  const mobileVerified =
    !user?.phoneNumberConfirmed && !mobileNeedsVerification;
  const isDataChanged =
    Object.keys(getChangedValues(formData, userData)).length > 0 ||
    mobileVerified;

  useEffect(() => {
    if (!emailNeedsVerification) {
      setShowEmailVerificationError(false);
    }

    if (!mobileNeedsVerification) {
      setShowPhoneVerificationError(false);
    }
  }, [emailNeedsVerification, mobileNeedsVerification]);

  useEffect(() => {
    const isSetUp = params.get("isSetUp");
    if (isSetUp) {
      setSelectedSection(settingSections.details);
    }
  }, [params]);

  useEffect(() => {
    if (user) {
      const neededData = {
        fullName: user.name || "",
        duePeriod: String(user.invoiceDueDate) || "",
        contactEmail: user.contactEmail || "",
        phoneNumber: user.contactMSISDN || "",
        notificationLang: user.language || "ENG",
        termsAccepted: user.isConsentOn || false,
        country: user.country || "",
        city: user.city || "",
        district: user.district || "",
        address: user.address || "",
        profileUrl: user.profileUrl || "",
      };

      let notificationChannels = { Email: false, Sms: false, Push: false };
      user.notificationConfig?.allowedChannels.forEach((item) => {
        notificationChannels[item] = true;
      });

      setUserData({ ...neededData, notificationChannels });
      setFormData({ ...neededData, notificationChannels });
      setMobileNeedsVerification(!user.phoneNumberConfirmed);
    }
  }, [user]);

  const handleCheckPermission = async () => {
    const currentPermission = Notification.permission;

    if (currentPermission === "granted") {
      setPermissionLoading(true);
      try {
        await requestNotificationPermission(ps10005000, decodedToken.buid);

        setFormData((prev) => ({
          ...prev,
          notificationChannels: {
            ...prev.notificationChannels,
            Push: true,
          },
        }));
        setConfirmModalToggle("");
      } catch (error) {
        console.error("Error requesting notification permission:", error);
      } finally {
        setPermissionLoading(false);
      }
    } else if (currentPermission === "denied") {
      setShowConfirmError(true);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/BusinessAccount/${decodedToken.buid}/ProfileImage`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${ps10005000}` },
          body: formData,
        },
      );

      if (res.ok) {
        fetchUser();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    setConfirmModalToggle("");
    setShowConfirmError(false);
  };

  const handleBack = () => {
    if (!!selectedSection) {
      setSelectedSection(null);
    } else {
      navigate(-1);
    }
  };

  const handleDataLoseConfirm = () => {
    setFormData(userData);
    setSelectedSection(null);
    setShowEmailVerificationError(false);
    setShowPhoneVerificationError(false);
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;

    setTermsIsChecked(checked);
    setTermsHasError(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "contactEmail") {
      setEmailNeedsVerification(
        isValidEmail(value) && value !== userData.contactEmail,
      );

      setShowEmailVerificationError(false);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value,
    }));
    setPhoneHasError(false);

    if (!mobileNeedsVerification) {
      setMobileNeedsVerification(
        isPossiblePhoneNumber(`+${value}`) && value !== userData.phoneNumber,
      );
    }

    setShowPhoneVerificationError(false);
  };

  const handleEmailToggle = (e) => {
    setFormData((prev) => ({
      ...prev,
      notificationChannels: {
        ...prev.notificationChannels,
        Email: e.target.checked,
      },
    }));
  };

  const handleSmsToggle = (e) => {
    setFormData((prev) => ({
      ...prev,
      notificationChannels: {
        ...prev.notificationChannels,
        Sms: e.target.checked,
      },
    }));
  };

  const handlePushToggle = async (e) => {
    if (e.target.checked && Notification.permission !== "granted") {
      if (isPWA) {
        if (Notification.permission === "denied") {
          setConfirmModalToggle("modal-confirm");
          return;
        }

        if (Notification.permission === "default") {
          setPermissionLoading(true);
          try {
            const data = await requestNotificationPermission(
              ps10005000,
              decodedToken.buid,
            );

            if (data && !data.success) {
              setPermissionLoading(false);
              return;
            }

            setFormData((prev) => ({
              ...prev,
              notificationChannels: {
                ...prev.notificationChannels,
                Push: true,
              },
            }));
          } catch (error) {
            console.error("Error requesting notification permission:", error);
          } finally {
            setPermissionLoading(false);
          }
        }
      } else {
        localStorage.removeItem("ps10003000");
        window.dispatchEvent(new Event("localStorageChangedPwa"));
        return;
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        notificationChannels: {
          ...prev.notificationChannels,
          Push: e.target.checked,
        },
      }));
    }
  };

  const handleValidate = () => {
    if (selectedSection === settingSections.details) {
      if (formData.fullName === "" || formData.contactEmail === "") {
        return;
      }

      if (userRole === "Provider" && formData.duePeriod === "") {
        return;
      }

      if (
        formData.phoneNumber === "" ||
        !isPossiblePhoneNumber("+" + formData.phoneNumber)
      ) {
        setPhoneHasError(true);
        return;
      }

      if (mobileNeedsVerification) {
        setShowPhoneVerificationError(true);
        return;
      }

      if (!isValidEmail(formData.contactEmail)) {
        return;
      }

      if (emailNeedsVerification) {
        setShowEmailVerificationError(true);
        return;
      }

      if (!termsIsChecked && !formData.termsAccepted) {
        setTermsHasError(true);
        return;
      }
    } else if (selectedSection === settingSections.location) {
      if (
        formData.city === "" ||
        formData.district === "" ||
        formData.address === ""
      ) {
        return;
      }
    } else {
      if (Object.keys(getChangedValues(formData, userData)).length === 0) {
        return;
      }
    }

    updateUser();
  };

  const updateUser = async () => {
    const changedValues = getChangedValues(formData, userData);
    const notificationConfigChanged =
      formData.notificationChannels !== userData.notificationChannels;

    const dataToPass = {
      country: changedValues.country,
      city: changedValues.city,
      address: changedValues.address,
      district: changedValues.district,
      name: changedValues.fullName,
      contactMSISDN: mobileVerified
        ? formData.phoneNumber
        : changedValues.phoneNumber,
      contactEmail: changedValues.contactEmail,
      invoiceDueDate: changedValues.duePeriod,
      language: changedValues.notificationLang,
    };

    try {
      await patch("/BusinessAccount", {
        id: decodedToken.buid,
        ...removeEmptyKeys(dataToPass),

        ...(notificationConfigChanged && {
          allowedChannels: Object.keys(formData.notificationChannels).filter(
            (key) => formData.notificationChannels[key] === true,
          ),
        }),
        ...(termsIsChecked && { isConsentOn: true }),
      });
      showStatusModal(t("userProfile.update_success"));

      fetchUser();
    } catch (e) {
      showStatusModal(e.message, null, true);
    }
  };

  return (
    <div className="profile-container">
      {(loading || permissionLoading) && <Loader />}
      <ModalConfirmation
        modalText={t("notifications_permission_title")}
        modalSubText={
          isAndroid
            ? t("notifications_enable_instruction_android")
            : t("notifications_enable_instruction_ios")
        }
        error={showConfirmError ? t("notifications_error_denied") : ""}
        modalValue={confirmModalToggle}
        confirm={handleCheckPermission}
        cancel={handleCancel}
        hideIcon={true}
      />
      <h3>{t("userProfile.page_header")}</h3>
      {!selectedSection && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Card
            variant="outlined"
            sx={{ display: "flex", alignItems: "center", p: 2, boxShadow: 3 }}
            onClick={() => setSelectedSection(settingSections.details)}
          >
            <Avatar
              src={user?.profileUrl}
              sx={{ width: 48, height: 48, mr: 2 }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("userProfile.card_account_title")} &gt;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("userProfile.card_account_subtitle")}
              </Typography>
            </Box>
          </Card>

          <Card
            variant="outlined"
            sx={{ display: "flex", alignItems: "center", p: 2, boxShadow: 3 }}
            onClick={() => setSelectedSection(settingSections.notifications)}
          >
            <NotificationsIcon sx={{ mr: 2, color: "gray" }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("userProfile.card_notifiactions_title")} &gt;
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("userProfile.card_notifiactions_subtitle")}
              </Typography>
            </Box>
          </Card>

          {userRole === "Provider" && (
            <Card
              variant="outlined"
              sx={{ display: "flex", alignItems: "center", p: 2, boxShadow: 3 }}
              onClick={() => setSelectedSection(settingSections.location)}
            >
              <LocationOnIcon sx={{ mr: 2, color: "gray" }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("userProfile.card_location_title")} &gt;
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("userProfile.card_location_subtitle")}
                </Typography>
              </Box>
            </Card>
          )}
        </Box>
      )}
      {selectedSection === settingSections.details && (
        <UserDetails
          formData={formData}
          userRole={userRole}
          termsIsChecked={termsIsChecked}
          phoneHasError={phoneHasError}
          termsHasError={termsHasError}
          showPhoneVerificationError={showPhoneVerificationError}
          showEmailVerificationError={showEmailVerificationError}
          emailNeedsVerification={emailNeedsVerification}
          mobileNeedsVerification={mobileNeedsVerification}
          setEmailNeedsVerification={setEmailNeedsVerification}
          setMobileNeedsVerification={setMobileNeedsVerification}
          handleInputChange={handleInputChange}
          handlePhoneInputChange={handlePhoneInputChange}
          handleCheckboxChange={handleCheckboxChange}
          handleImageUpload={handleImageUpload}
        />
      )}

      {selectedSection === settingSections.notifications && (
        <NotificationSettings
          formData={formData}
          emailEnabled={formData.notificationChannels.Email}
          smsEnabled={formData.notificationChannels.Sms}
          pushEnabled={formData.notificationChannels.Push}
          setEmailEnabled={handleEmailToggle}
          setSmsEnabled={handleSmsToggle}
          setPushEnabled={handlePushToggle}
          handleSelectChange={handleSelectChange}
        />
      )}

      {selectedSection === settingSections.location && (
        <LoactionDetails
          userCountryCode={userData.country}
          formData={formData}
          handleInputChange={handleInputChange}
        />
      )}
      {!!selectedSection && (
        <BottomFixedContainer>
          <BackButton
            onClick={handleBack}
            onModalConfirm={handleDataLoseConfirm}
            showDataLoseWarning={isDataChanged}
          />
          <CustomButton onClick={handleValidate} disabled={!isDataChanged}>
            {t("update")}
          </CustomButton>
        </BottomFixedContainer>
      )}
    </div>
  );
};

export default Profile;
